import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    FileText,
    Image as ImageIcon,
    Terminal,
    ArrowRightLeft,
    Video,
    Calculator,
    Search,
    Wrench,
    Files,
    Palette,
    Globe,
    BrainCircuit,
    Atom,
    LucideIcon,
    BookOpen,
    Sparkles,
    ShieldCheck,
    Compass
} from 'lucide-react';
import { ToolCategory } from '@/data/tools';

interface CategoryCardProps {
    category: ToolCategory;
    icon: LucideIcon;
    index: number;
}

const CategoryCard = ({ category, icon: Icon, index }: CategoryCardProps) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
        >
            <Link
                to={`/category/${category.toLowerCase().replace(/ /g, '-')}-tools`}
                className="group relative flex items-center gap-4 p-5 md:p-6 bg-white dark:bg-slate-900/60 backdrop-blur-xl border border-slate-100 dark:border-white/5 rounded-3xl hover:shadow-2xl hover:shadow-primary/5 hover:border-primary/20 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
            >
                <div className="p-3.5 bg-primary/5 group-hover:bg-primary group-hover:text-white rounded-2xl text-primary transition-all duration-300 shadow-sm shrink-0">
                    <Icon size={20} />
                </div>
                <div className="flex-1 flex flex-col justify-center">
                    <h3 className="font-bold text-sm md:text-base text-gray-900 dark:text-gray-100 group-hover:text-primary transition-colors mb-1">{category}</h3>
                    <p className="text-[10px] md:text-xs text-gray-500 font-medium uppercase tracking-wider opacity-60">Explore</p>
                </div>
                
                {/* Decorative background element */}
                <div className="absolute -bottom-4 -right-4 w-12 h-12 bg-primary/5 rounded-full blur-xl group-hover:bg-primary/10 transition-colors"></div>
            </Link>
        </motion.div>
    );
};

const CategoryGrid = () => {
    const categories: { id: ToolCategory; icon: LucideIcon }[] = [
        { id: 'Text', icon: FileText },
        { id: 'Image', icon: ImageIcon },
        { id: 'Dev', icon: Terminal },
        { id: 'Science', icon: Atom },

        { id: 'Converters', icon: ArrowRightLeft },
        { id: 'Network', icon: Globe },
        { id: 'PDF', icon: Files },
        { id: 'Design', icon: Palette },
        { id: 'Utility', icon: Wrench },
        { id: 'Calculator', icon: Calculator },
        { id: 'SEO', icon: Search },
        { id: 'Media', icon: Video },
        { id: 'Brain Games', icon: BrainCircuit },
        { id: 'Fun', icon: Sparkles },
        { id: 'Discovery', icon: BookOpen },
        { id: 'Security', icon: ShieldCheck },
        { id: 'Earth & Space', icon: Globe },
        { id: 'Explore', icon: Compass },
    ];


    return (
        <section id="categories" className="container mx-auto px-6 py-12 md:py-20 max-w-[1240px] overflow-hidden">
            <div className="flex flex-col items-center mb-10 md:mb-16 text-center">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="px-4 py-1.5 bg-primary/5 dark:bg-primary/10 text-primary rounded-full text-xs font-bold mb-4 uppercase tracking-widest border border-primary/10 dark:border-primary/20"
                >
                    Collections
                </motion.div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white tracking-tight leading-tight">Browse by Category</h2>
                <p className="text-gray-500 dark:text-gray-400 max-w-2xl text-base md:text-lg font-medium">
                    Find the perfect tool for your workflow from our curated collections.
                </p>
            </div>

            {/* Mobile Scroller / Desktop Grid */}
            <div className="flex md:grid overflow-x-auto md:overflow-x-visible pb-8 md:pb-0 scrollbar-hide snap-x snap-mandatory md:snap-none grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-6 px-1">
                {categories.map((cat, index) => (
                    <div key={cat.id} className="snap-center">
                        <CategoryCard
                            category={cat.id}
                            icon={cat.icon}
                            index={index}
                        />
                    </div>
                ))}
            </div>
            
            {/* Visual indicator for mobile scrolling */}
            <div className="md:hidden flex justify-center gap-1 mt-2">
                <div className="w-8 h-1 bg-primary rounded-full"></div>
                <div className="w-2 h-1 bg-gray-200 dark:bg-gray-800 rounded-full"></div>
                <div className="w-1 h-1 bg-gray-200 dark:bg-gray-800 rounded-full"></div>
            </div>
        </section>
    );
};

export default CategoryGrid;

